import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    Avatar,
    Divider,
    Collapse,
    CircularProgress
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import { IoClose as Close } from 'react-icons/io5';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import LabelCardService from '@Helpers/URLHelpers/WeboutService/LabelCardService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './LabelCardForm.scss';

export default function LabelCardForm(props) {
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [remarks, setRemarks] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const errorHandler = new ErrorHandler();
    const labelCardService = new LabelCardService();
    const { currentProjectId } = useReduxProject();
    const className = 'labelCardForm';

    const handleNameChange = (e) => {
        setName(DOMPurify.sanitize(e.target.value));
    };

    const handleDescriptionChange = (e) => {
        setDescription(DOMPurify.sanitize(e.target.value));
    };

    const handleRemarksChange = (e) => {
        setRemarks(DOMPurify.sanitize(e.target.value));
    };

    const getLabelColor = (name) => {
        if (!name) return '#1976d2';
        const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
        const colors = ['#1976d2', '#2e7d32', '#8e24aa', '#d32f2f', '#ef6c00', '#0288d1'];
        return colors[hash % colors.length];
    };

    const handleSubmit = async (e) => {
        try {
            e?.preventDefault();
            setLoading(true);
            const labelCardObj = {
                projectId: currentProjectId,
                name,
                description: description || undefined,
                remarks: showAdvanced ? remarks : undefined
            };
            await labelCardService.createLabelCard(labelCardObj);
            await props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.closePopup}
            maxWidth="sm"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`
            }}
        >
            <DialogTitle className={`${className}__header`}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                            className={`${className}__header__avatar`}
                            style={{ background: getLabelColor(name) }}
                        >
                            <LabelIcon />
                        </Avatar>
                        <Typography variant="h6" className={`${className}__header__title`}>
                            Create Label Card
                        </Typography>
                    </Box>
                    <IconButton onClick={props.closePopup} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent className={`${className}__content`}>
                <form onSubmit={handleSubmit} id="label-card-form">
                    {/* Name Section */}
                    <Box className={`${className}__section`}>
                        <TextField
                            label="Label Name"
                            placeholder="Enter label name"
                            value={name}
                            onChange={handleNameChange}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                            helperText={
                                name.length >= 180 ? `${name.length}/200 characters` : ''
                            }
                            FormHelperTextProps={{
                                className: name.length >= 200 ? `${className}__charLimit` : ''
                            }}
                        />
                    </Box>

                    {/* Description Section */}
                    <Box className={`${className}__section`}>
                        <TextField
                            label="Description"
                            placeholder="Enter a brief description"
                            value={description}
                            onChange={handleDescriptionChange}
                            fullWidth
                            multiline
                            rows={3}
                            helperText="Optional: Describe what this label represents"
                        />
                    </Box>

                    {/* Advanced Details Toggle */}
                    <Box className={`${className}__toggleSection`}>
                        <Button
                            variant="text"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            className={`${className}__toggleBtn`}
                        >
                            {showAdvanced ? 'Hide' : 'Show'} Additional Details
                        </Button>
                    </Box>

                    {/* Advanced Section */}
                    <Collapse in={showAdvanced}>
                        <Box className={`${className}__section`}>
                            <Typography variant="subtitle2" className={`${className}__sectionTitle`}>
                                Additional Notes
                            </Typography>
                            <TextField
                                label="Remarks"
                                placeholder="Enter any additional remarks"
                                value={remarks}
                                onChange={handleRemarksChange}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Box>
                    </Collapse>
                </form>
            </DialogContent>

            <Divider />

            <DialogActions className={`${className}__footer`}>
                <Button
                    onClick={props.closePopup}
                    disabled={loading}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="label-card-form"
                    variant="contained"
                    disabled={loading || !name.trim()}
                    startIcon={loading && <CircularProgress size={16} />}
                >
                    {loading ? 'Creating...' : 'Create Label Card'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
